import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  Tag,
  Text,
  useBreakpointValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { isValidUSPhoneNumber } from '@utils/validatePhone';
import React, { useEffect, useRef, useState } from 'react';
import { FiRefreshCw, FiSend } from 'react-icons/fi';
import ChatMessage from './ChatMessage';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  isLoading?: boolean;
  role?: 'user' | 'assistant';
  showContactButtons?: boolean;
}

interface OzempicChatInterfaceProps {
  dynamicPhoneNumber: string;
}

const examplePrompts = [
  'Does Medicare cover Ozempic?',
  'What are common side effects of Ozempic?',
  'How much does Ozempic cost with Medicare?',
  "Is Ozempic right for me if I'm turning 65?",
];

const OzempicChatInterface: React.FC<OzempicChatInterfaceProps> = ({ dynamicPhoneNumber }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false); // True when waiting for AI response stream to start/end
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  // State for phone number collection
  const [collectingPhoneNumberFor, setCollectingPhoneNumberFor] = useState<{
    aiPromptMessageId: string;
    contactType: 'call' | 'text';
  } | null>(null);
  const [phoneNumberInput, setPhoneNumberInput] = useState('');
  const phoneNumberInputRef = useRef<HTMLInputElement>(null);

  // Responsive placeholder
  const placeholderText = useBreakpointValue({
    base: 'Type here...',
    md: 'Type your question here...',
  });

  const sendButtonText = useBreakpointValue({
    base: '', // No text on mobile
    md: 'Send', // Text on medium screens and up
  });

  const initialChatHeight = '200px'; // Smaller height when empty
  const activeChatHeight = '450px'; // Full height when messages are present

  const scrollToBottom = (forceScroll = false) => {
    const container = messagesContainerRef.current;
    if (container) {
      // Check if user is scrolled near the bottom, or if forceScroll is true
      const isScrolledToBottom = container.scrollHeight - container.clientHeight <= container.scrollTop + 100; // 100px tolerance
      if (forceScroll || isScrolledToBottom) {
        container.scrollTop = container.scrollHeight;
      }
    }
  };

  useEffect(() => {
    // Scroll to bottom when a new message is added by the user,
    // or when an AI message starts (the empty one with isLoading:true)
    // We use forceScroll = true here to ensure visibility of the latest interaction start.
    scrollToBottom(true);
  }, [messages.length]);

  // Effect to scroll to bottom when the last AI message finishes loading (and buttons might appear)
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      // If the last message is an AI message and it just finished loading
      if (lastMessage.sender === 'ai' && !lastMessage.isLoading) {
        // This ensures that after the AI message (and its potential buttons)
        // are set in state and rendered, a scroll occurs.
        scrollToBottom(true);
      }
    }
  }, [messages]);

  useEffect(() => {
    if (collectingPhoneNumberFor && phoneNumberInputRef.current) {
      // Use a short timeout to ensure the element is fully in the DOM and focusable
      setTimeout(() => phoneNumberInputRef.current?.focus(), 0);
    }
  }, [collectingPhoneNumberFor]);

  const handleContactPreferenceSelected = (clickedMessageId: string, contactType: 'call' | 'text') => {
    const aiPromptText =
      contactType === 'call'
        ? 'Great! To arrange your call, please enter your phone number below.'
        : 'Great! To arrange your text message, please enter your phone number below.';

    const newAiPromptMessageId = Date.now().toString();

    setMessages((prevMessages) => {
      const updatedMessages = prevMessages.map((msg) =>
        msg.id === clickedMessageId ? { ...msg, showContactButtons: false } : msg
      );
      const aiPromptMessage: Message = {
        id: newAiPromptMessageId,
        text: aiPromptText,
        sender: 'ai',
        isLoading: false,
        role: 'assistant',
        showContactButtons: false,
      };
      return [...updatedMessages, aiPromptMessage];
    });

    setCollectingPhoneNumberFor({ aiPromptMessageId: newAiPromptMessageId, contactType });
    setPhoneNumberInput('');
  };

  const handleSendMessage = async (textToSend?: string) => {
    const currentMessageText = textToSend || inputValue.trim();
    if (!currentMessageText) return;

    // When a new message is sent, ensure all previous messages hide their contact buttons.
    // This will be reinforced when the AI message finalizes, but good to do here too for user messages.
    setMessages((prevMessages) => prevMessages.map((msg) => ({ ...msg, showContactButtons: false })));

    const userMessage: Message = {
      id: Date.now().toString(),
      text: currentMessageText,
      sender: 'user',
      role: 'user',
    };

    // Add user message and an initial empty AI message for streaming
    const aiMessageId = (Date.now() + 1).toString();
    const initialAiMessage: Message = {
      id: aiMessageId,
      text: '',
      sender: 'ai',
      role: 'assistant',
      isLoading: true,
    };
    setMessages((prev) => [...prev, userMessage, initialAiMessage]);

    if (!textToSend) {
      setInputValue('');
    }
    setIsLoading(true);

    try {
      const apiMessages = [...messages, userMessage].map((msg) => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text,
      }));

      const response = await fetch('/api/ozempic-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: apiMessages, dynamicPhoneNumber }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'API request failed' }));
        throw new Error(errorData.error || `API request failed: ${response.status}`);
      }
      if (!response.body) throw new Error('Response body is null');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let contactOfferDetectedInStream = false;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        accumulatedText += decoder.decode(value, { stream: true });
        // Check for contact offer marker during streaming
        if (accumulatedText.includes('[CONTACT_OFFER]')) {
          contactOfferDetectedInStream = true;
        }
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.id === aiMessageId
              ? { ...msg, text: accumulatedText.replace('[CONTACT_OFFER]', ''), isLoading: true }
              : msg
          )
        );
        // No forced scroll during streaming, relies on user being at bottom or manual scroll
        scrollToBottom();
      }

      let finalDisplayMessage = accumulatedText;
      let shouldShowButtonsForThisMessage = false;
      if (contactOfferDetectedInStream || accumulatedText.includes('[CONTACT_OFFER]')) {
        shouldShowButtonsForThisMessage = true;
        finalDisplayMessage = accumulatedText.replace('[CONTACT_OFFER]', '').trim();
      }

      setMessages((prevMessages) =>
        prevMessages.map(
          (msg) =>
            msg.id === aiMessageId
              ? {
                  ...msg,
                  text: finalDisplayMessage,
                  isLoading: false,
                  showContactButtons: shouldShowButtonsForThisMessage,
                }
              : { ...msg, showContactButtons: false } // Hide buttons for all other messages
        )
      );
    } catch (error) {
      console.error('Error sending message or processing stream:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === aiMessageId ? { ...msg, text: `Error: ${errorMessage}`, isLoading: false } : msg
        )
      );
      toast({
        title: 'Error',
        description: `Failed to get response from AI: ${errorMessage}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setInputValue('');
    toast({
      title: 'New chat started.',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleSubmitPhoneNumber = () => {
    const trimmedPhoneNumber = phoneNumberInput.trim();

    if (!trimmedPhoneNumber) {
      toast({
        title: 'Phone number required',
        description: 'Please enter your phone number.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Basic US phone number validation (10 digits, optional common separators)
    if (!isValidUSPhoneNumber(trimmedPhoneNumber)) {
      toast({
        title: 'Invalid phone number format',
        description: 'Please enter a valid 10-digit US phone number.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const contactType = collectingPhoneNumberFor?.contactType;
    const finalConfirmationText =
      contactType === 'call'
        ? `Thank you! We've received your number (${trimmedPhoneNumber}) and will call you shortly.`
        : `Thank you! We've received your number (${trimmedPhoneNumber}) and will text you shortly.`;

    const finalAiMessage: Message = {
      id: Date.now().toString(),
      text: finalConfirmationText,
      sender: 'ai',
      isLoading: false,
      role: 'assistant',
      showContactButtons: false,
    };

    setMessages((prevMessages) => [...prevMessages, finalAiMessage]);
    setCollectingPhoneNumberFor(null);
    setPhoneNumberInput('');

    fetch('/api/notify-slack-contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phoneNumber: trimmedPhoneNumber,
        contactPreference: contactType,
        chatbotType: 'Ozempic',
        source: 'Ozempic Chatbot',
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'API request failed with no JSON body' }));
          throw new Error(`API request failed: ${response.status} - ${errorData?.error || response.statusText}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log('Slack notification API call successful:', data?.message);
        toast({
          title: 'Contact preference submitted!',
          description: `We will be in touch via ${contactType} shortly.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      })
      .catch((error) => {
        console.error('Error calling Slack notification API:', error);
        toast({
          title: 'Submission warning',
          description: 'There was an issue notifying our team, but your request is logged.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      });
  };

  return (
    <VStack
      spacing={6}
      align="stretch"
      width="100%"
      borderWidth="1px"
      borderRadius="lg"
      py={6}
      px={{ base: 0, md: 6 }}
      boxShadow="md"
    >
      <Flex justifyContent="space-between" alignItems="center" mb={2} px={{ base: 4, md: 0 }}>
        <Text fontWeight="bold" fontSize="xl">
          Ozempic Assistant
        </Text>
        <Button
          leftIcon={<Icon as={FiRefreshCw} fontSize={{ base: 'md', md: 'lg' }} />}
          onClick={handleNewChat}
          size={{ base: 'sm', md: 'sm' }}
          variant="outline"
          colorScheme="purple"
        >
          New Chat
        </Button>
      </Flex>

      <VStack
        ref={messagesContainerRef}
        spacing={4}
        height={messages.length === 0 ? initialChatHeight : activeChatHeight}
        minHeight={initialChatHeight}
        overflowY="auto"
        py={{ base: 1, md: 4 }}
        px={{ base: 0, md: 4 }}
        borderWidth="1px"
        borderRadius="md"
        bg="gray.50"
        justifyContent={messages.length === 0 ? 'center' : 'flex-start'}
        transition="height 0.3s ease-in-out"
      >
        {messages.length === 0 && (
          <Box textAlign="center" px={4}>
            {' '}
            <Text fontSize="xl" fontWeight="medium" color="gray.700">
              {' '}
              Ask a question about Ozempic and Medicare.
            </Text>
            <Text fontSize="md" color="gray.500" mt={2}>
              {' '}
              Or click on a common question below.
            </Text>
          </Box>
        )}
        {messages.map((msg) => (
          <React.Fragment key={msg.id}>
            {' '}
            <ChatMessage
              message={msg}
              onPreferCall={msg.showContactButtons ? () => handleContactPreferenceSelected(msg.id, 'call') : undefined}
              onPreferText={msg.showContactButtons ? () => handleContactPreferenceSelected(msg.id, 'text') : undefined}
            />
            {collectingPhoneNumberFor && collectingPhoneNumberFor.aiPromptMessageId === msg.id && msg.sender === 'ai' && (
              <Box
                pl={{ base: 'calc(24px + 8px + 8px)', md: 'calc(32px + 12px + 16px)' }}
                pr={{ base: 2, md: 4 }}
                pb={3}
                pt={2}
                w="100%"
                maxW={{ base: '95%', md: '80%' }}
                ml="auto"
                mr="auto"
                alignSelf={'flex-start'}
              >
                <HStack
                  as="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmitPhoneNumber();
                  }}
                >
                  <Input
                    ref={phoneNumberInputRef}
                    placeholder="Enter phone number"
                    value={phoneNumberInput}
                    onChange={(e) => setPhoneNumberInput(e.target.value)}
                    type="tel"
                    size="md"
                  />
                  <Button type="submit" colorScheme="purple" size="md">
                    {' '}
                    Submit
                  </Button>
                </HStack>
              </Box>
            )}
          </React.Fragment>
        ))}
      </VStack>

      <Box px={{ base: 4, md: 0 }}>
        <Text fontSize="md" color="gray.600" mb={3}>
          Common questions:
        </Text>
        <Flex wrap="wrap" gap={3}>
          {examplePrompts.map((prompt) => (
            <Tag
              key={prompt}
              size="lg"
              variant="subtle"
              colorScheme="cyan"
              onClick={() => {
                if (!isLoading) handleSendMessage(prompt);
              }}
              cursor={isLoading ? 'not-allowed' : 'pointer'}
              _hover={isLoading ? {} : { bg: 'cyan.100' }}
              style={isLoading ? { opacity: 0.6 } : {}}
              fontSize="15px"
            >
              {prompt}
            </Tag>
          ))}
        </Flex>
      </Box>

      <HStack
        as="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSendMessage();
        }}
        spacing={3}
        px={{ base: 4, md: 0 }}
      >
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={placeholderText}
          isDisabled={isLoading}
          size="lg"
          fontSize="md"
        />
        <Button
          // Only provide leftIcon if there's text (i.e., not on mobile)
          leftIcon={sendButtonText ? <Icon as={FiSend} fontSize={{ base: 'md', md: 'lg' }} /> : undefined}
          type="submit"
          colorScheme="purple"
          isLoading={isLoading}
          isDisabled={isLoading || !inputValue.trim()}
          size={{ base: 'md', md: 'lg' }}
          // px=3 on base with size=md should make it square when icon is the only child
          px={{ base: 3, md: 6 }}
          aria-label="Send message"
        >
          {/* If sendButtonText is empty (mobile), render Icon as child. Otherwise, render text. */}
          {sendButtonText ? sendButtonText : <Icon as={FiSend} fontSize="md" />}
        </Button>
      </HStack>
    </VStack>
  );
};

export default OzempicChatInterface;
